import React, { useMemo } from 'react';
import { previewProgressRatio } from '../../utils/fideliteGain';

/**
 * Aperçu « expérience client » simplifiée (données déjà calculées côté F1/F2.2c).
 * Priorité visuelle colonne droite — pas une carte d’administration.
 */
export default function ClientPreviewCard({ preview }) {
  const ratio = useMemo(() => {
    if (!preview || preview.multiActiveHint) return null;
    if (preview.nextRewardPoints == null) return null;
    return previewProgressRatio(preview.balanceAfter, preview.nextRewardPoints);
  }, [preview]);

  const pct = ratio != null ? Math.round(ratio * 100) : null;

  if (!preview) return null;

  return (
    <section
      className="rounded-2xl border border-purple-100 bg-gradient-to-b from-[#4A148C] to-[#6A1B9A] p-1 shadow-md"
      data-testid="client-preview-card"
    >
      <div className="rounded-[0.9rem] bg-white p-4 space-y-3">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-[#4A148C]/80">
          Aperçu pour vos clients
        </p>

        <div className="flex items-start gap-2.5">
          <span
            className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-full bg-purple-100 text-[#4A148C] text-sm font-bold"
            aria-hidden
          >
            ★
          </span>
          <div className="min-w-0">
            <h3 className="text-base font-bold text-gray-900 leading-tight">
              {preview.nomProgramme}
            </h3>
            {preview.messageBienvenue && (
              <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                {preview.messageBienvenue}
              </p>
            )}
          </div>
        </div>

        {preview.programmePaused && (
          <p className="text-sm text-orange-700 bg-orange-50 rounded-lg px-3 py-2">
            Programme en pause : pas de nouveaux points pour le moment.
          </p>
        )}

        <ul className="space-y-1.5">
          {preview.bullets.slice(0, 3).map((b, idx) => (
            <li key={`${idx}-${b.slice(0, 20)}`} className="flex gap-2 text-sm text-gray-800">
              <span className="text-emerald-600 font-bold shrink-0" aria-hidden>
                ✓
              </span>
              <span>{b}</span>
            </li>
          ))}
        </ul>

        {!preview.hasReward && (
          <p className="text-sm text-gray-600">
            Les points s’accumulent, mais aucune récompense n’est encore échangeable.
          </p>
        )}

        <div className="rounded-xl bg-gray-50 border border-gray-100 p-3 space-y-2.5">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
            Exemple
          </p>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Commande</span>
            <span className="font-semibold text-gray-900">{preview.orderEur} €</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Vous gagnez</span>
            <span className="font-semibold text-gray-900">
              {preview.pointsGagnes != null
                ? `${preview.pointsGagnes} point${preview.pointsGagnes > 1 ? 's' : ''}`
                : '—'}
            </span>
          </div>

          {preview.balanceAfter != null && (
            <>
              <div className="flex justify-between text-sm pt-1 border-t border-gray-200/80">
                <span className="text-gray-600">Solde</span>
                <span className="font-bold text-[#4A148C]">
                  {preview.balanceAfter} point{preview.balanceAfter > 1 ? 's' : ''}
                </span>
              </div>

              {pct != null && preview.nextRewardPoints != null && (
                <div className="space-y-1.5 pt-1" data-testid="client-preview-progress">
                  <div className="h-2.5 rounded-full bg-purple-100 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-[#4A148C] transition-all"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-600 leading-snug">
                    {preview.missingPoints === 0
                      ? 'Vous pouvez obtenir votre prochaine récompense.'
                      : preview.missingPoints != null
                        ? `Plus que ${preview.missingPoints} point${
                            preview.missingPoints > 1 ? 's' : ''
                          } avant votre prochaine récompense.`
                        : null}
                    {preview.nextRewardShortLabel ? (
                      <>
                        {' '}
                        <span className="font-medium text-gray-800">
                          ({preview.nextRewardShortLabel})
                        </span>
                      </>
                    ) : null}
                  </p>
                </div>
              )}

              {preview.hasReward &&
                preview.canChooseRedeem === false &&
                preview.missingPoints == null && (
                  <p className="text-xs text-gray-500">
                    Plusieurs récompenses sont actives : le choix au paiement arrivera bientôt.
                  </p>
                )}
            </>
          )}
        </div>

        <p className="text-[10px] text-gray-400 text-center">
          Exemple fictif — aucun client réel
        </p>
      </div>
    </section>
  );
}
